import React, { useState, useEffect } from 'react';
import { ArrowLeft, Settings as SettingsIcon, Globe, Bell, Download, Eye, CheckCircle, AlertCircle, Mail, Loader } from 'lucide-react';
import API_BASE_URL from '../config/api';

const API_URL = API_BASE_URL;

function SettingsPage({ onBack }) {
  const [preferences, setPreferences] = useState({
    language: 'en',
    notifications: {
      email: true,
      analysisComplete: true,
      lowCredits: true,
      newFeatures: false
    },
    privacy: {
      showEmail: false,
      showAnalyses: true,
      allowDataExport: true
    }
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(true);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  /**
   * FUNCTION 1: Fetch settings from backend
   * PURPOSE: Load user's saved preferences on page load
   */
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/user/settings`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.preferences) {
        setPreferences(data.preferences);
        // Sync language to localStorage
        if (data.preferences.language) {
          localStorage.setItem('language', data.preferences.language);
        }
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      showMessage('error', 'Failed to load settings. Using defaults.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * FUNCTION 2: Save to backend (CORE AUTO-SAVE)
   * PURPOSE: Persist preferences to database immediately
   */
  const saveToBackend = async (newPreferences) => {
    try {
      const response = await fetch(`${API_URL}/api/user/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ preferences: newPreferences })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // If email notifications changed, sync with Resend
      if (newPreferences.notifications.email !== preferences.notifications.email) {
        await syncResendPreferences(newPreferences.notifications);
      }

      return { success: true };
    } catch (error) {
      console.error('Save failed:', error);
      throw error;
    }
  };

  /**
   * FUNCTION 3: Sync email preferences with Resend
   * PURPOSE: Update Resend contact preferences when email settings change
   */
  const syncResendPreferences = async (notificationPrefs) => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ emailNotifications: notificationPrefs })
      });

      if (!response.ok) {
        console.warn('Resend sync failed, but preferences saved');
      }
    } catch (error) {
      console.warn('Resend sync error:', error);
      // Non-critical - don't block the user
    }
  };

  /**
   * FUNCTION 4: Show message helper
   * PURPOSE: Display feedback to user
   */
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  /**
   * FUNCTION 5: Handle language change
   * PURPOSE: Change UI language and save to backend + localStorage
   */
  const handleLanguageChange = async (newLang) => {
    const newPrefs = { ...preferences, language: newLang };
    setPreferences(newPrefs);

    try {
      setSaving(true);
      // Save to localStorage for immediate UI update
      localStorage.setItem('language', newLang);

      // Save to backend
      await saveToBackend(newPrefs);

      const langName = languages.find(l => l.code === newLang)?.name || newLang;
      showMessage('success', `Language changed to ${langName}`);
    } catch (error) {
      showMessage('error', 'Failed to save language preference');
      // Revert on failure
      setPreferences(preferences);
      localStorage.setItem('language', preferences.language);
    } finally {
      setSaving(false);
    }
  };

  /**
   * FUNCTION 6: Toggle email notifications
   * PURPOSE: Enable/disable email notifications with master toggle logic
   */
  const toggleNotification = async (key) => {
    let newNotifications;

    // Master toggle logic
    if (key === 'email') {
      const newEmailState = !preferences.notifications.email;
      newNotifications = {
        email: newEmailState,
        // When turning OFF master, disable all sub-toggles
        analysisComplete: newEmailState ? preferences.notifications.analysisComplete : false,
        lowCredits: newEmailState ? preferences.notifications.lowCredits : false,
        newFeatures: newEmailState ? preferences.notifications.newFeatures : false
      };
    } else {
      // Individual toggle - only works if master is ON
      if (!preferences.notifications.email) {
        return; // Silently ignore if master is off
      }

      newNotifications = {
        ...preferences.notifications,
        [key]: !preferences.notifications[key]
      };
    }

    const newPrefs = {
      ...preferences,
      notifications: newNotifications
    };

    setPreferences(newPrefs);

    try {
      setSaving(true);
      await saveToBackend(newPrefs);
      showMessage('success', 'Notification preferences updated');
    } catch (error) {
      showMessage('error', 'Failed to save notification preferences');
      // Revert on failure
      setPreferences(preferences);
    } finally {
      setSaving(false);
    }
  };

  /**
   * FUNCTION 7: Toggle privacy settings
   * PURPOSE: Control privacy/visibility settings with auto-save
   */
  const togglePrivacy = async (key) => {
    const newPrivacy = {
      ...preferences.privacy,
      [key]: !preferences.privacy[key]
    };

    const newPrefs = {
      ...preferences,
      privacy: newPrivacy
    };

    setPreferences(newPrefs);

    try {
      setSaving(true);
      await saveToBackend(newPrefs);
      showMessage('success', 'Privacy settings updated');
    } catch (error) {
      showMessage('error', 'Failed to save privacy settings');
      // Revert on failure
      setPreferences(preferences);
    } finally {
      setSaving(false);
    }
  };

  /**
   * FUNCTION 8: Export user data (GDPR compliance)
   * PURPOSE: Download all user data as JSON file
   */
  const handleExport = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/user/export`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Export failed: HTTP ${response.status}`);
      }

      // Get filename from headers or generate
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `proprscout-data-${new Date().toISOString().split('T')[0]}.json`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      showMessage('success', 'Data export downloaded successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      showMessage('error', 'Failed to export data. Please try again or contact support.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Dashboard</span>
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg">
                <SettingsIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900 font-heading">Settings</h1>
                <p className="text-slate-600">Customize your app experience and preferences</p>
              </div>
            </div>
            {saving && (
              <div className="flex items-center gap-2 text-primary-600">
                <Loader className="w-4 h-4 animate-spin" />
                <span className="text-sm font-medium">Saving...</span>
              </div>
            )}
          </div>
        </div>

        {/* Message Banner */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' :
              message.type === 'error' ? 'bg-red-50 border border-red-200 text-red-800' :
                'bg-blue-50 border border-blue-200 text-blue-800'
            }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : message.type === 'error' ? (
              <AlertCircle className="w-5 h-5 text-red-600" />
            ) : (
              <Bell className="w-5 h-5 text-blue-600" />
            )}
            <span className="text-sm font-medium">{message.text}</span>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <Loader className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
            <div className="text-slate-500">Loading settings...</div>
          </div>
        ) : (
          <>
            {/* 2-Column Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Language & Display */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-900 font-heading mb-6 flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Language & Display
                </h2>
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">Language</label>
                  <select
                    value={preferences.language}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    disabled={saving}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-slate-500">Changes are saved automatically</p>
                </div>
              </div>

              {/* Privacy & Data */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-900 font-heading mb-6 flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Privacy & Data
                </h2>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Eye className="w-4 h-4 text-slate-600" />
                      <div>
                        <div className="text-sm font-medium text-slate-900">Show email in profile</div>
                        <div className="text-xs text-slate-500">Make your email visible to others</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => togglePrivacy('showEmail')}
                      disabled={saving}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${preferences.privacy.showEmail ? 'bg-primary-600' : 'bg-slate-300'
                        } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${preferences.privacy.showEmail ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Eye className="w-4 h-4 text-slate-600" />
                      <div>
                        <div className="text-sm font-medium text-slate-900">Show analyses publicly</div>
                        <div className="text-xs text-slate-500">Make your analyses visible</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => togglePrivacy('showAnalyses')}
                      disabled={saving}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${preferences.privacy.showAnalyses ? 'bg-primary-600' : 'bg-slate-300'
                        } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${preferences.privacy.showAnalyses ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                    </button>
                  </div>
                </div>
                <div className="p-4 bg-primary-50 rounded-xl border border-primary-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Download className="w-5 h-5 text-primary-600" />
                      <div>
                        <div className="text-sm font-medium text-slate-900 mb-1">Export your data</div>
                        <div className="text-xs text-slate-600">Download all your data in JSON format</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleExport}
                      disabled={saving}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download className="w-4 h-4" />
                      Export
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Email Notifications - Full Width */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-6">
                <Mail className="w-5 h-5 text-primary-600" />
                <h2 className="text-xl font-bold text-slate-900 font-heading">Email Notifications</h2>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">Connected to Resend</span>
              </div>

              {/* Master Toggle */}
              <div className="mb-6 p-4 bg-gradient-to-r from-primary-50 to-blue-50 border-2 border-primary-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-primary-100 rounded-lg">
                      <Bell className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <div className="text-base font-bold text-slate-900">All email notifications</div>
                      <div className="text-sm text-slate-600">Master toggle for all emails</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleNotification('email')}
                    disabled={saving}
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${preferences.notifications.email ? 'bg-primary-600' : 'bg-slate-300'
                      } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${preferences.notifications.email ? 'translate-x-8' : 'translate-x-1'
                      }`} />
                  </button>
                </div>
              </div>

              {/* Individual Toggles */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { key: 'analysisComplete', label: 'Analysis complete', desc: 'When your analysis is ready', icon: CheckCircle, color: 'green' },
                  { key: 'lowCredits', label: 'Low credits warning', desc: 'When credits are running low', icon: AlertCircle, color: 'yellow' },
                  { key: 'newFeatures', label: 'New features', desc: 'Product updates and news', icon: Bell, color: 'blue' }
                ].map(({ key, label, desc, icon: Icon, color }) => (
                  <div
                    key={key}
                    className={`p-4 rounded-xl border-2 transition-all ${preferences.notifications.email
                        ? 'bg-white border-slate-200'
                        : 'bg-slate-100 border-slate-200 opacity-50 cursor-not-allowed'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className={`p-2 bg-${color}-100 rounded-lg`}>
                        <Icon className={`w-4 h-4 text-${color}-600`} />
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleNotification(key)}
                        disabled={!preferences.notifications.email || saving}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${preferences.notifications[key] && preferences.notifications.email
                            ? 'bg-primary-600'
                            : 'bg-slate-300'
                          } ${(!preferences.notifications.email || saving) ? 'cursor-not-allowed opacity-50' : ''}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${preferences.notifications[key] && preferences.notifications.email ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                      </button>
                    </div>
                    <div className="text-sm font-medium text-slate-900">{label}</div>
                    <div className="text-xs text-slate-500 mt-1">{desc}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-700">
                  <strong>ℹ️ Auto-save:</strong> All changes are saved automatically. Email templates are sent via Resend.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default SettingsPage;
